import { STATIC_VALUE_SOURCE, isComparableOperator } from '../../codecs/flat-comparison-filter';
import type { OperatorValuePatch, ValueOption } from './types';

/**
 * What a comparison's value can be read from, and what each one is called.
 *
 * The labels are lower case because the select reads inside a sentence:
 * "plan is equal to [the current person's attribute] plan".
 */
const VALUE_SOURCES = {
  [STATIC_VALUE_SOURCE]: { label: 'the value', placeholder: undefined },
  customer_attribute: { label: "the current person's attribute", placeholder: 'Profile attribute' },
  event_attribute: { label: 'the trigger event attribute', placeholder: 'Trigger attribute' },
  journey_attribute: { label: 'the journey attribute', placeholder: 'Journey attribute' },
} as const satisfies Record<string, { label: string; placeholder: string | undefined }>;

/** A value source a surface can declare. */
export type ValueSource = keyof typeof VALUE_SOURCES;

/** Labels a surface uses in place of the defaults, by source. */
export type ValueSourceLabels = Readonly<Partial<Record<ValueSource, string>>>;

/**
 * The select's options for the sources a surface declares, in the order it
 * declares them. A source in `labels` takes that label instead of its default.
 */
export function valueSourceOptions(declared: readonly ValueSource[], labels?: ValueSourceLabels): ValueOption[] {
  return declared.map((source) => ({ value: source, label: labels?.[source] ?? VALUE_SOURCES[source].label }));
}

/** A source's label, or the source itself when it is not one this select names. */
export function valueSourceLabel(valueSource: string): string {
  const known = Object.entries(VALUE_SOURCES).find(([source]) => source === valueSource);
  return known === undefined ? valueSource : known[1].label;
}

/**
 * What the value field says while empty, for a value that names an attribute
 * rather than holding a literal. Undefined for the literal, which keeps the
 * editor's own placeholder.
 */
export function valueSourcePlaceholder(valueSource: string | undefined): string | undefined {
  if (
    valueSource === 'customer_attribute' ||
    valueSource === 'event_attribute' ||
    valueSource === 'journey_attribute'
  ) {
    return VALUE_SOURCES[valueSource].placeholder;
  }
  return undefined;
}

/**
 * Whether an operator can compare against another attribute on this row:
 * one of `comparableOperators` where the surface narrows them, otherwise any
 * operator that can compare.
 */
function canCompare(operator: string, comparableOperators: readonly string[] | undefined): boolean {
  return comparableOperators == null ? isComparableOperator(operator) : comparableOperators.includes(operator);
}

/** Whether the row shows its value-source select: the surface offers a choice and the operator can compare. */
export function showsValueSource(
  operator: string,
  options: readonly ValueOption[],
  comparableOperators?: readonly string[],
): boolean {
  return options.length > 0 && canCompare(operator, comparableOperators);
}

/**
 * The source change an operator change brings with it.
 *
 * An operator that cannot compare drops the source back to the literal. Without
 * that, changing back to an operator that can compare would bring back a
 * source the user left behind.
 */
export function valueSourcePatchForOperator(
  operator: string,
  valueSource: string | undefined,
  options: readonly ValueOption[] | undefined,
  comparableOperators?: readonly string[],
): OperatorValuePatch {
  if (options == null || valueSource == null || valueSource === STATIC_VALUE_SOURCE) {
    return {};
  }
  return canCompare(operator, comparableOperators) ? {} : { valueSource: STATIC_VALUE_SOURCE };
}

/**
 * The patch a pick in the value-source select writes, or null for no change.
 *
 * A move between two attribute sources clears the value, because the name of
 * one kind of attribute is not the name of another. A move into or out of the
 * literal keeps it.
 */
export function valueSourceChangePatch(current: string | undefined, next: string | null): OperatorValuePatch | null {
  const from = current ?? STATIC_VALUE_SOURCE;
  if (next == null || next === from) {
    return null;
  }
  if (from !== STATIC_VALUE_SOURCE && next !== STATIC_VALUE_SOURCE) {
    return { valueSource: next, value: '' };
  }
  return { valueSource: next };
}

/** What decides which value sources a campaign's attribute condition offers. */
export type CampaignValueSourceFlags = {
  isWebhookTriggered: boolean;
  /** Whether the campaign has a trigger event, whose attributes a value can read. */
  readsTriggerEvent: boolean;
};

const ALL_CAMPAIGN_SOURCES = [
  STATIC_VALUE_SOURCE,
  'customer_attribute',
  'event_attribute',
  'journey_attribute',
] as const satisfies readonly ValueSource[];

const WITHOUT_TRIGGER_EVENT_SOURCES = [
  STATIC_VALUE_SOURCE,
  'customer_attribute',
  'journey_attribute',
] as const satisfies readonly ValueSource[];

const WEBHOOK_SOURCES = [STATIC_VALUE_SOURCE, 'event_attribute'] as const satisfies readonly ValueSource[];

/**
 * The value-source select of a workflow step's attribute condition, by
 * campaign. The webhook check comes first:
 *
 * - A webhook-triggered campaign offers the literal and the webhook's
 *   attribute, which reads "the trigger attribute".
 * - A campaign with no trigger event leaves the trigger event attribute out.
 * - Every other campaign offers the literal, the person's attribute, the
 *   trigger event attribute and the journey attribute.
 */
export function campaignValueSourceOptions(campaign: CampaignValueSourceFlags): ValueOption[] {
  if (campaign.isWebhookTriggered) {
    return valueSourceOptions(WEBHOOK_SOURCES, { event_attribute: 'the trigger attribute' });
  }
  return valueSourceOptions(campaign.readsTriggerEvent ? ALL_CAMPAIGN_SOURCES : WITHOUT_TRIGGER_EVENT_SOURCES);
}

/**
 * What a comparison's value can read on a campaign triggered by an object, a
 * relationship or a relationship's attribute, in the order the select lists
 * them.
 */
const OBJECT_RELATIONSHIP_CAMPAIGN_SOURCES = [
  STATIC_VALUE_SOURCE,
  'customer_attribute',
  'relationship_attribute',
  'trigger_object_attribute',
  'trigger_relationship_attribute',
  'trigger_person_attribute',
] as const;

type ObjectRelationshipCampaignSource = (typeof OBJECT_RELATIONSHIP_CAMPAIGN_SOURCES)[number];

/**
 * The sources an object campaign, or a campaign whose audience is the trigger
 * person alone, can read: there is no second person to read from.
 */
const SELF_AUDIENCE_CAMPAIGN_SOURCES = [
  STATIC_VALUE_SOURCE,
  'customer_attribute',
  'relationship_attribute',
  'trigger_object_attribute',
] as const satisfies readonly ObjectRelationshipCampaignSource[];

/** What `audience.type` records an audience of the trigger person alone as. */
const SELF_AUDIENCE_TYPE = 0;

/** What `relationship_campaign.type` records a relationship trigger as. */
const RELATIONSHIP_TRIGGER = { created: 0, updated: 2 } as const;

/**
 * What the select calls each source on these campaigns. The trigger's two
 * person-scoped sources name the person the trigger fired on, which a
 * relationship change words differently from a relationship being added.
 */
function objectRelationshipSourceLabels(objectSingularName: string, relationshipTriggerType: number) {
  const objectName = objectSingularName.toLowerCase();
  const isChange = relationshipTriggerType === RELATIONSHIP_TRIGGER.updated;
  return {
    [STATIC_VALUE_SOURCE]: 'the value',
    customer_attribute: "the current person's attribute",
    relationship_attribute: "the current person's relationship attribute",
    trigger_object_attribute: `the trigger ${objectName} attribute`,
    trigger_relationship_attribute: isChange
      ? `the relationship attribute of the person whose relationship to the ${objectName} changed`
      : `the person added to the ${objectName}'s relationship attribute`,
    trigger_person_attribute: isChange
      ? `the person attribute of the person whose relationship to the ${objectName} changed`
      : `the person added to the ${objectName}'s attribute`,
  } satisfies Record<ObjectRelationshipCampaignSource, string>;
}

function objectRelationshipOptions(
  sources: readonly ObjectRelationshipCampaignSource[],
  labels: ReturnType<typeof objectRelationshipSourceLabels>,
): ValueOption[] {
  return sources.map((source) => ({ value: source, label: labels[source] }));
}

/**
 * The value-source select on a campaign triggered by an object: the value,
 * the current person and their relationship, or the trigger object.
 */
export function objectCampaignValueSourceOptions(objectSingularName: string): ValueOption[] {
  return objectRelationshipOptions(
    SELF_AUDIENCE_CAMPAIGN_SOURCES,
    objectRelationshipSourceLabels(objectSingularName, RELATIONSHIP_TRIGGER.created),
  );
}

/**
 * The value-source select on a campaign triggered by a relationship. It also
 * reads the person the trigger fired on and their relationship, unless the
 * audience is that person alone.
 *
 * `audienceType` is the campaign's `audience.type`, and
 * `relationshipTriggerType` its `relationship_campaign.type`.
 */
export function relationshipCampaignValueSourceOptions(
  audienceType: number,
  relationshipTriggerType: number,
  objectSingularName: string,
): ValueOption[] {
  const labels = objectRelationshipSourceLabels(objectSingularName, relationshipTriggerType);
  return objectRelationshipOptions(
    audienceType === SELF_AUDIENCE_TYPE ? SELF_AUDIENCE_CAMPAIGN_SOURCES : OBJECT_RELATIONSHIP_CAMPAIGN_SOURCES,
    labels,
  );
}

/**
 * The value-source select on a campaign triggered by a relationship's
 * attribute. Its relationship is its own trigger, so it reads what a
 * relationship campaign whose audience is the trigger person reads.
 */
export function relSegAttrCampaignValueSourceOptions(objectSingularName: string): ValueOption[] {
  return relationshipCampaignValueSourceOptions(SELF_AUDIENCE_TYPE, RELATIONSHIP_TRIGGER.created, objectSingularName);
}
